import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Switch,
  Tooltip,
  Typography,
  message,
} from 'antd'
import { CheckCircleFilled } from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import fetchAPI from '../../../lib/utils/fetch-api'

const { Text, Link: ExternalLink } = Typography

type Provider = 'mango' | 'avito' | 'calltouch' | 'yandex_direct'

type IntegrationState = {
  connected: boolean
  values: Record<string, string | boolean | number | null>
  secrets: Record<string, string | null>
}

type Overview = {
  head_office: { id: number; name: string | null } | null
  organization: { id: number; name: string | null } | null
  integrations: Record<Provider, IntegrationState>
  yandex_oauth?: { configured: boolean; pending_token: boolean }
}

type CheckResult = { ok: boolean; message: string; details?: Record<string, any> }

type FieldMeta = {
  name: string
  label: string
  secret?: boolean
  switch?: boolean
  required?: boolean
  placeholder?: string
  officeSelect?: boolean
  extra?: string
}

type ProviderMeta = {
  key: Provider
  title: string
  target: 'office' | 'organization'
  hint: React.ReactNode
  fields: FieldMeta[]
}

const PROVIDERS: ProviderMeta[] = [
  {
    key: 'mango',
    title: 'Mango',
    target: 'office',
    hint: (
      <>Код АТС и ключ подписи — в личном кабинете Mango Office: «Интеграции» → «API коннектор».</>
    ),
    fields: [
      {
        name: 'mango_domain',
        label: 'Mango Office: домен',
        placeholder: 'vpbx000000000.mangosip.ru',
      },
      {
        name: 'mango_api',
        label: 'Mango Office: уникальный код АТС',
        secret: true,
        required: true,
      },
      {
        name: 'mango_salt',
        label: 'Mango Office: ключ для создания подписи',
        secret: true,
        required: true,
      },
      { name: 'mango_create_orders', label: 'Создавать обращения при звонках', switch: true },
    ],
  },
  {
    key: 'avito',
    title: 'Авито',
    target: 'organization',
    hint: (
      <>
        Номер профиля указан в{' '}
        <ExternalLink href="https://www.avito.ru/profile" target="_blank">
          личном кабинете
        </ExternalLink>
        , client_id и client_secret — на странице{' '}
        <ExternalLink href="https://www.avito.ru/professionals/api" target="_blank">
          «Интеграции»
        </ExternalLink>
        .
      </>
    ),
    fields: [
      { name: 'avito_account_id', label: 'Авито: номер профиля' },
      { name: 'avito_client_id', label: 'Авито: client_id', required: true },
      { name: 'avito_client_secret', label: 'Авито: client_secret', secret: true, required: true },
    ],
  },
  {
    key: 'yandex_direct',
    title: 'Яндекс Директ',
    target: 'organization',
    hint: (
      <>
        Войдите через Яндекс (или вставьте OAuth-токен). Укажите ID Турбо-страниц/лендингов с
        формами — заявки будут приходить как обращения в ERP. Для агентства — Client-Login.
      </>
    ),
    fields: [
      {
        name: 'yandex_direct_oauth_token',
        label: 'OAuth-токен',
        secret: true,
        required: true,
        extra: 'Удобнее получить кнопкой «Войти через Яндекс»',
      },
      {
        name: 'yandex_direct_client_login',
        label: 'Client-Login (для агентств)',
        placeholder: 'логин-рекламодателя',
      },
      {
        name: 'yandex_direct_turbo_page_ids',
        label: 'ID Турбо-страниц / лендингов',
        placeholder: '123456, 789012',
        extra: 'Через запятую. Нужны, чтобы подтягивать заявки из форм.',
      },
      {
        name: 'yandex_direct_office_id',
        label: 'Офис для обращений',
        officeSelect: true,
        extra: 'Куда создавать обращения из лидов',
      },
    ],
  },
  {
    key: 'calltouch',
    title: 'Calltouch',
    target: 'office',
    hint: <>Site ID и токен API — в личном кабинете Calltouch: «Интеграции» → «API».</>,
    fields: [
      { name: 'calltouch_site_id', label: 'Calltouch: site ID', required: true },
      { name: 'calltouch_token', label: 'Calltouch: токен API', secret: true, required: true },
    ],
  },
]

type Props = {
  onChanged?: () => void
}

export function HomeIntegrationsConnect({ onChanged }: Props) {
  const location = useLocation()
  const navigate = useNavigate()
  const [overview, setOverview] = useState<Overview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [active, setActive] = useState<ProviderMeta | null>(null)
  const [checking, setChecking] = useState(false)
  const [saving, setSaving] = useState(false)
  const [checkResult, setCheckResult] = useState<CheckResult | null>(null)
  const canSave = Boolean(checkResult?.ok)
  const [form] = Form.useForm()
  const [officeOptions, setOfficeOptions] = useState<Array<{ value: number; label: string }>>([])

  const load = useCallback(async () => {
    setError(null)
    try {
      const res = await fetchAPI('/api/home/integrations')
      if (res?.errors) {
        setError(res.errors?.[0]?.detail || 'Не удалось загрузить интеграции')
        return
      }
      setOverview(res?.data || null)
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки')
    } finally {
      setLoading(false)
    }
  }, [])

  const loadOffices = useCallback(async () => {
    try {
      const res = await fetchAPI('/api/home/company-structure')
      const structure = res?.data
      const options: Array<{ value: number; label: string }> = []
      const pushOrg = (org: any) => {
        for (const office of org?.offices || []) {
          options.push({
            value: Number(office.id),
            label: `${org.name} → ${office.name}`,
          })
        }
      }
      for (const brand of structure?.brands || []) {
        for (const org of brand.organizations || []) pushOrg(org)
      }
      for (const org of structure?.organizations_without_brand || []) pushOrg(org)
      setOfficeOptions(options)
    } catch {
      /* non-blocking */
    }
  }, [])

  useEffect(() => {
    load()
    loadOffices()
  }, [load, loadOffices])

  const open = useCallback(
    async (meta: ProviderMeta, preset?: Record<string, any>) => {
      const state = overview?.integrations?.[meta.key]
      const initial: Record<string, any> = { ...(preset || {}) }
      for (const field of meta.fields) {
        if (preset && preset[field.name] != null) continue
        if (field.secret) initial[field.name] = ''
        else if (field.switch) initial[field.name] = Boolean(state?.values?.[field.name])
        else if (field.officeSelect) {
          initial[field.name] =
            state?.values?.[field.name] ?? overview?.head_office?.id ?? undefined
        } else initial[field.name] = state?.values?.[field.name] ?? ''
      }
      form.resetFields()
      form.setFieldsValue(initial)
      setCheckResult(null)
      setActive(meta)

      if (meta.key === 'yandex_direct' && overview?.yandex_oauth?.pending_token) {
        try {
          const res = await fetchAPI('/api/home/integrations/yandex_direct/oauth/consume', {
            method: 'POST',
            body: '{}',
          })
          const token = res?.data?.yandex_direct_oauth_token
          if (token) {
            form.setFieldsValue({ yandex_direct_oauth_token: token })
            message.success('Токен Яндекса получен — проверьте и сохраните настройки')
          }
        } catch {
          /* ignore */
        }
      }
    },
    [form, overview],
  )

  // Возврат из OAuth
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('integrations') !== 'yandex_direct') return
    const status = params.get('yandex_oauth')
    const msg = params.get('message')
    if (status === 'error') {
      message.error(msg || 'Не удалось войти через Яндекс')
    }
    const meta = PROVIDERS.find(p => p.key === 'yandex_direct')
    if (meta) {
      void open(meta)
    }
    navigate({ pathname: location.pathname, search: '' }, { replace: true })
  }, [location.pathname, location.search, navigate, open])

  const close = () => {
    setActive(null)
    setCheckResult(null)
  }

  const handleCheck = async () => {
    if (!active) return
    setChecking(true)
    setCheckResult(null)
    try {
      const snapshot = JSON.stringify(form.getFieldsValue(true))
      const res = await fetchAPI(`/api/home/integrations/${active.key}/check`, {
        method: 'POST',
        body: snapshot,
      })
      if (JSON.stringify(form.getFieldsValue(true)) !== snapshot) return
      if (res?.errors) {
        setCheckResult({ ok: false, message: res.errors?.[0]?.detail || 'Проверка не удалась' })
        return
      }
      const result: CheckResult = res?.data
      setCheckResult(result)
      const accountId = result?.details?.account_id
      if (active.key === 'avito' && accountId && !form.getFieldValue('avito_account_id')) {
        form.setFieldsValue({ avito_account_id: String(accountId) })
      }
    } catch (e: any) {
      setCheckResult({ ok: false, message: e?.message || 'Проверка не удалась' })
    } finally {
      setChecking(false)
    }
  }

  const handleSave = async () => {
    if (!active || !checkResult?.ok) return
    try {
      await form.validateFields()
    } catch {
      return
    }
    setSaving(true)
    try {
      const res = await fetchAPI(`/api/home/integrations/${active.key}`, {
        method: 'PUT',
        body: JSON.stringify(form.getFieldsValue(true)),
      })
      if (res?.errors) {
        const detail = res.errors?.[0]?.detail || 'Не удалось сохранить'
        setCheckResult({ ok: false, message: detail })
        message.error(detail)
        return
      }
      message.success(`${active.title}: настройки сохранены`)
      close()
      await load()
      onChanged?.()
    } catch (e: any) {
      message.error(e?.message || 'Ошибка сохранения')
    } finally {
      setSaving(false)
    }
  }

  const startYandexOAuth = () => {
    window.location.href = '/api/home/integrations/yandex_direct/oauth/start?return=/home'
  }

  const providerOrder = useMemo(
    () =>
      [
        PROVIDERS[0],
        PROVIDERS[1],
        PROVIDERS[2],
        PROVIDERS[3],
      ] as ProviderMeta[],
    [],
  )

  if (loading) return <Spin size="small" />
  if (error) return <Alert type="error" showIcon message={error} />

  const activeState = active ? overview?.integrations?.[active.key] : null
  const targetName = active
    ? active.target === 'office'
      ? overview?.head_office?.name || 'головной офис'
      : overview?.organization?.name || 'организация'
    : ''

  const renderButton = (meta: ProviderMeta) => {
    const connected = Boolean(overview?.integrations?.[meta.key]?.connected)
    return (
      <Tooltip
        key={meta.key}
        title={connected ? 'Подключено — нажмите, чтобы изменить' : undefined}
      >
        <Button
          type={connected ? 'default' : 'primary'}
          ghost={!connected}
          icon={connected ? <CheckCircleFilled style={{ color: '#52c41a' }} /> : undefined}
          onClick={() => open(meta)}
        >
          {connected ? `${meta.title} · Подключено` : `Подключить ${meta.title}`}
        </Button>
      </Tooltip>
    )
  }

  return (
    <>
      <Space wrap size={8}>
        {providerOrder.map(renderButton)}
      </Space>

      <Modal
        open={Boolean(active)}
        title={active ? `Подключить ${active.title}` : ''}
        onCancel={close}
        destroyOnClose
        width={active?.key === 'yandex_direct' ? 560 : 520}
        footer={[
          <Button key="check" onClick={handleCheck} loading={checking} disabled={saving}>
            Проверить подключение
          </Button>,
          <Button key="cancel" onClick={close} disabled={saving}>
            Отмена
          </Button>,
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            loading={saving}
            disabled={!canSave || checking}
            title={canSave ? undefined : 'Сначала проверьте подключение'}
          >
            Сохранить
          </Button>,
        ]}
      >
        {active && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text type="secondary">
              {active.hint} Настройки сохранятся в{' '}
              {active.target === 'office' ? 'головном офисе' : 'организации'} «{targetName}».
            </Text>

            {active.key === 'yandex_direct' && (
              <Alert
                type="info"
                showIcon
                message="Вход через Яндекс"
                description={
                  overview?.yandex_oauth?.configured ? (
                    <Space direction="vertical" size={8}>
                      <Text>
                        Нажмите кнопку — откроется Яндекс. После разрешения вернётесь сюда с
                        токеном.
                      </Text>
                      <Button type="default" onClick={startYandexOAuth}>
                        Войти через Яндекс
                      </Button>
                    </Space>
                  ) : (
                    <Text>
                      OAuth на сервере не настроен (YANDEX_OAUTH_CLIENT_ID /
                      YANDEX_OAUTH_CLIENT_SECRET). Можно вставить токен вручную.
                    </Text>
                  )
                }
              />
            )}

            <Form
              form={form}
              layout="vertical"
              autoComplete="off"
              requiredMark
              onValuesChange={() => setCheckResult(null)}
            >
              {active.fields.map(field => {
                const masked = field.secret ? activeState?.secrets?.[field.name] : null
                return (
                  <Form.Item
                    key={field.name}
                    name={field.name}
                    label={field.label}
                    valuePropName={field.switch ? 'checked' : 'value'}
                    required={field.required}
                    extra={
                      field.extra ||
                      (masked ? `Сохранено: ${masked}. Оставьте пустым, чтобы не менять.` : undefined)
                    }
                    rules={
                      field.required && !masked
                        ? [{ required: true, whitespace: !field.officeSelect, message: 'Заполните поле' }]
                        : undefined
                    }
                  >
                    {field.switch ? (
                      <Switch />
                    ) : field.officeSelect ? (
                      <Select
                        allowClear
                        placeholder="Офис для обращений"
                        options={
                          officeOptions.length
                            ? officeOptions
                            : overview?.head_office
                              ? [
                                  {
                                    value: overview.head_office.id,
                                    label: overview.head_office.name || 'Головной офис',
                                  },
                                ]
                              : []
                        }
                        showSearch
                        optionFilterProp="label"
                      />
                    ) : field.secret ? (
                      <Input.Password
                        placeholder={masked || field.placeholder}
                        autoComplete="new-password"
                        onChange={() => setCheckResult(null)}
                      />
                    ) : (
                      <Input
                        placeholder={field.placeholder}
                        onChange={() => setCheckResult(null)}
                      />
                    )}
                  </Form.Item>
                )
              })}
            </Form>
            {checkResult && (
              <Alert
                type={checkResult.ok ? 'success' : 'error'}
                showIcon
                message={checkResult.message}
              />
            )}
            {active.key === 'yandex_direct' && overview?.organization?.id && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Вебхук (опционально):{' '}
                <code>
                  POST /api/web-hooks/yandex-direct?organization_id={overview.organization.id}
                </code>
              </Text>
            )}
          </Space>
        )}
      </Modal>
    </>
  )
}

export default HomeIntegrationsConnect
